import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as programsApi from '../api/programs';

export function AdminPrograms() {
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    siteId: '',
    assetId: '',
    effectiveFrom: '',
    effectiveTo: '',
    customAttributes: {}
  });

  const loadPrograms = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await programsApi.getPrograms(selectedOrg.id);
      setPrograms(data);
      if (data.length > 0 && !selectedProgram) {
        setSelectedProgram(data[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, selectedProgram]);

  const loadProgramDetails = useCallback(async () => {
    if (!selectedOrg || !selectedProgram) return;

    try {
      const { fieldDefinitions: fields } = await programsApi.getProgram(selectedOrg.id, selectedProgram.id);
      setFieldDefinitions(fields);
      
      const enrollmentData = await programsApi.getEnrollments(selectedOrg.id, selectedProgram.id);
      setEnrollments(enrollmentData);
    } catch (err) {
      setError(err.message);
    }
  }, [selectedOrg, selectedProgram]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  useEffect(() => {
    if (selectedProgram) {
      loadProgramDetails();
    }
  }, [selectedProgram, loadProgramDetails]);

  const handleProgramChange = (programId) => {
    const program = programs.find(p => p.id === programId);
    setSelectedProgram(program);
  };

  const handleOpenEnroll = () => {
    const defaultAttrs = {};
    fieldDefinitions.forEach(f => {
      if (f.fieldType === 'boolean') defaultAttrs[f.fieldName] = false;
      else if (f.fieldType === 'number') defaultAttrs[f.fieldName] = 0;
      else defaultAttrs[f.fieldName] = '';
    });
    
    setEnrollForm({
      siteId: '',
      assetId: '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '',
      customAttributes: defaultAttrs
    });
    setShowEnrollModal(true);
  };

  const handleOpenEdit = (enrollment) => {
    setEditingEnrollment(enrollment);
    setEnrollForm({
      siteId: enrollment.siteId || '',
      assetId: enrollment.assetId || '',
      effectiveFrom: enrollment.effectiveFrom || '',
      effectiveTo: enrollment.effectiveTo || '',
      customAttributes: { ...enrollment.customAttributes }
    });
    setShowEditModal(true);
  };

  const handleEnroll = async () => {
    if (!selectedOrg || !selectedProgram || saving) return;
    setSaving(true);
    try {
      await programsApi.createEnrollment(selectedOrg.id, selectedProgram.id, {
        siteId: enrollForm.siteId || null,
        assetId: enrollForm.assetId || null,
        effectiveFrom: enrollForm.effectiveFrom,
        effectiveTo: enrollForm.effectiveTo || null,
        customAttributes: enrollForm.customAttributes
      });
      setShowEnrollModal(false);
      loadProgramDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEnrollment = async () => {
    if (!selectedOrg || !editingEnrollment || saving) return;
    setSaving(true);
    try {
      await programsApi.updateEnrollment(selectedOrg.id, editingEnrollment.id, {
        effectiveTo: enrollForm.effectiveTo || null,
        customAttributes: enrollForm.customAttributes
      });
      setShowEditModal(false);
      loadProgramDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCustomAttrChange = (fieldName, value) => {
    setEnrollForm(prev => ({
      ...prev,
      customAttributes: {
        ...prev.customAttributes,
        [fieldName]: value
      }
    }));
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view programs.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Programs & Enrollments"
        subtitle="Manage program enrollments and custom attributes"
      />

      <div style={styles.toolbar}>
        <select
          value={selectedProgram?.id || ''}
          onChange={(e) => handleProgramChange(e.target.value)}
          style={styles.select}
          data-testid="program-select"
        >
          {programs.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {isAdmin && (
          <button
            onClick={handleOpenEnroll}
            style={styles.enrollButton}
            data-testid="enroll-button"
          >
            + New Enrollment
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading programs...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="enrollments-table">
            <thead>
              <tr>
                <th style={styles.th}>Site/Asset</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Effective From</th>
                <th style={styles.th}>Effective To</th>
                {fieldDefinitions.map(f => (
                  <th key={f.id} style={styles.th}>{f.label}</th>
                ))}
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map(enrollment => (
                <tr key={enrollment.id} style={styles.tr} data-testid={`enrollment-${enrollment.id}`}>
                  <td style={styles.td}>
                    {enrollment.siteId && <div>Site: {enrollment.siteId}</div>}
                    {enrollment.assetId && <div>Asset: {enrollment.assetId}</div>}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: enrollment.status === 'active' ? '#c6f6d5' : '#fed7d7',
                      color: enrollment.status === 'active' ? '#276749' : '#c53030'
                    }}>
                      {enrollment.status}
                    </span>
                  </td>
                  <td style={styles.td}>{enrollment.effectiveFrom}</td>
                  <td style={styles.td}>{enrollment.effectiveTo || '-'}</td>
                  {fieldDefinitions.map(f => (
                    <td key={f.id} style={styles.td}>
                      {renderFieldValue(enrollment.customAttributes[f.fieldName], f.fieldType)}
                    </td>
                  ))}
                  <td style={styles.td}>
                    {isAdmin && (
                      <button
                        onClick={() => handleOpenEdit(enrollment)}
                        style={styles.editButton}
                        data-testid={`edit-${enrollment.id}`}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {enrollments.length === 0 && (
                <tr>
                  <td colSpan={5 + fieldDefinitions.length} style={styles.emptyState}>
                    No enrollments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(showEnrollModal || showEditModal) && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="enrollment-modal">
            <h3 style={styles.modalTitle}>
              {showEditModal ? 'Edit Enrollment' : 'New Enrollment'}
            </h3>
            {!showEditModal && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Site ID</label>
                  <input
                    type="text"
                    value={enrollForm.siteId}
                    onChange={(e) => setEnrollForm(prev => ({ ...prev, siteId: e.target.value }))}
                    style={styles.input}
                    placeholder="e.g., site-1"
                    data-testid="enroll-site-input"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Asset ID (optional)</label>
                  <input
                    type="text"
                    value={enrollForm.assetId}
                    onChange={(e) => setEnrollForm(prev => ({ ...prev, assetId: e.target.value }))}
                    style={styles.input}
                    placeholder="e.g., asset-1"
                    data-testid="enroll-asset-input"
                  />
                </div>
              </>
            )}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Effective From</label>
                <input
                  type="date"
                  value={enrollForm.effectiveFrom}
                  onChange={(e) => setEnrollForm(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                  style={styles.input}
                  disabled={showEditModal}
                  data-testid="enroll-from-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Effective To (optional)</label>
                <input
                  type="date"
                  value={enrollForm.effectiveTo}
                  onChange={(e) => setEnrollForm(prev => ({ ...prev, effectiveTo: e.target.value }))}
                  style={styles.input}
                  data-testid="enroll-to-input"
                />
              </div>
            </div>
            <h4 style={styles.sectionTitle}>Custom Attributes</h4>
            {fieldDefinitions.map(field => (
              <div key={field.id} style={styles.formGroup}>
                <label style={styles.label}>
                  {field.label}
                  {field.required && <span style={styles.required}>*</span>}
                </label>
                {renderFieldInput(field, enrollForm.customAttributes[field.fieldName], handleCustomAttrChange)}
              </div>
            ))}
            <div style={styles.modalActions}>
              <button
                onClick={() => { setShowEnrollModal(false); setShowEditModal(false); }}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={showEditModal ? handleUpdateEnrollment : handleEnroll}
                disabled={saving}
                style={styles.submitButton}
                data-testid="save-enrollment-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function renderFieldValue(value, fieldType) {
  if (value === undefined || value === null) return '-';
  if (fieldType === 'boolean') return value ? 'Yes' : 'No';
  if (fieldType === 'number') return value.toLocaleString();
  return String(value);
}

function renderFieldInput(field, value, onChange) {
  if (field.fieldType === 'boolean') {
    return (
      <select
        value={value ? 'true' : 'false'}
        onChange={(e) => onChange(field.fieldName, e.target.value === 'true')}
        style={styles.input}
        data-testid={`field-${field.fieldName}`}
      >
        <option value="false">No</option>
        <option value="true">Yes</option>
      </select>
    );
  }
  if (field.fieldType === 'number') {
    return (
      <input
        type="number"
        value={value || ''}
        onChange={(e) => onChange(field.fieldName, parseFloat(e.target.value) || 0)}
        style={styles.input}
        data-testid={`field-${field.fieldName}`}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(field.fieldName, e.target.value)}
      style={styles.input}
      data-testid={`field-${field.fieldName}`}
    />
  );
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  enrollButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  editButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #3182ce',
    background: 'white',
    color: '#3182ce',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  sectionTitle: {
    margin: '1.5rem 0 1rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#4a5568'
  },
  formGroup: {
    marginBottom: '1rem',
    flex: 1
  },
  formRow: {
    display: 'flex',
    gap: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  required: {
    color: '#c53030',
    marginLeft: '0.25rem'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
